import { zodResolver } from '@hookform/resolvers/zod'
import { observer } from 'mobx-react-lite'
import { NavArrowDown, NavArrowUp, Xmark } from 'iconoir-react'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { getLocationIdsToLoad } from 'app/components/UserLocationSelector'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useQuickContactLookup } from 'app/hooks/useQuickContactLookup'
import {
  firstProblemField,
  locationChoice,
  type QuickCreateOutput,
  type QuickCreateValues,
  quickCreateSchema,
  resolveHeading,
} from 'app/lib/contactQuickEntry'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore, useLocationStore } from 'app/stores/StoreContext'
import type { ContactJson, LocationJson } from 'app/types/entities'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug } from 'lib/logging'
import { digitsOnly } from 'lib/phone'
import { ContactSuggestionList, type ContactSuggestionListHandle } from './ContactSuggestionList'
import { DuplicateBanner } from './DuplicateBanner'
import { LocationPicker } from './LocationPicker'
import { OptionPicker } from './OptionPicker'
import { OptionRow } from './PickerButton'
import { QuickContactOptions } from './QuickContactOptions'

const LEAD_STATUS_OPTIONS = [`Needs Action`, `In Progress`, `Scheduled`, `Closed Lost`].map((value) => ({
  label: value,
  value,
}))

type Props = {
  onCreate: (contact: ContactJson) => void
  /**
   * Invoked when the user picks an existing contact we surfaced instead of
   * creating a new one. Each consumer decides what "open" means (the contacts
   * page swaps its drawer to that contact). Without it the record opens in a
   * new tab.
   */
  onExistingContact?: (contact: ContactJson) => void
  /** The caller's number when creating from a call or a text thread. */
  defaultCellPhone?: string
  defaultName?: string
  /** The location the page is already filtered to, so a multi-location user never has to pick it. */
  defaultLocationId?: string
}

/**
 * Keyboard-first contact creator, opened with `showModal` (`hideTitle`, since the
 * heading input is the title). The heading takes a name or a phone number, because
 * the front desk often starts from caller ID: the number finds the caller if they
 * are already here, and Tab moves it into the phone field so the name goes in the
 * heading. A name is always required to save. Fast path: name, Tab, phone, Enter.
 *
 * This file wires the form to its parts. The rules live in `contactQuickEntry`,
 * the lookups in `useQuickContactLookup`, and the pieces in this folder.
 */
// observer: reads a store in render, so the component must be an observer.
export const QuickCreateContactModal = observer(function QuickCreateContactModal({
  onCreate,
  onExistingContact,
  defaultCellPhone,
  defaultName,
  defaultLocationId,
}: Props) {
  const user = useUser()
  if (!user) return null

  const isSMC = isSMCOrg(user.orgId)
  const workspace = useWorkspace()
  const contactStore = useContactStore()
  const locationStore = useLocationStore()

  const form = useForm<QuickCreateValues>({
    resolver: zodResolver(quickCreateSchema) as never,
    defaultValues: {
      name: defaultName ?? ``,
      cellPhone: defaultCellPhone ? (formatPhoneNumber(defaultCellPhone) ?? ``) : ``,
      email: ``,
      locationId: ``,
      becameLeadDate: moment().startOf(`day`).toDate(),
      leadStatus: `Needs Action`,
      utmSource: ``,
      utmMedium: ``,
      utmCampaign: ``,
      channel: isSMC ? `Digital Marketing` : undefined,
    },
    mode: `onSubmit`,
  })
  const { register, handleSubmit, control, watch, setValue, setFocus, getValues, formState } = form

  const [locations, setLocations] = useState<LocationJson[]>()
  const [showMore, setShowMore] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const suggestionsRef = useRef<ContactSuggestionListHandle>(null)

  // React Hook Form's watch API is intentionally live and must not be memoized.
  // oxlint-disable-next-line react/incompatible-library
  const name = watch(`name`)
  const cellPhone = watch(`cellPhone`)
  const email = watch(`email`)
  const locationId = watch(`locationId`)
  const { suggestions, duplicate, dismiss, recheckDuplicate } = useQuickContactLookup({
    name,
    cellPhone,
    email,
    locationId,
  })

  // Only the locations this user may work in. One workspace location, or access
  // to one, means the location line never appears.
  useEffect(() => {
    bg(async () => {
      const result = await locationStore.fetchList()
      const hits = ((result.data as { locations?: { hits?: LocationJson[] } })?.locations?.hits ??
        locationStore.hits) as LocationJson[]
      const accessibleIds = getLocationIdsToLoad({
        user: user as Parameters<typeof getLocationIdsToLoad>[0][`user`],
        currentWorkspaceId: workspace?.id,
      })
      const { options, preselectedId } = locationChoice(hits, accessibleIds, defaultLocationId)
      setLocations(options)
      if (preselectedId) setValue(`locationId`, preselectedId)
    })
  }, [])

  /** Tab out of a heading that holds a number: it moves down into the phone field. */
  const moveHeadingPhone = () => {
    const resolved = resolveHeading({ name: getValues(`name`), cellPhone: digitsOnly(getValues(`cellPhone`)) })
    if (resolved.name === getValues(`name`)) return
    setValue(`cellPhone`, formatPhoneNumber(resolved.cellPhone) ?? resolved.cellPhone)
    setValue(`name`, ``)
  }

  const openExisting = (contact: ContactJson) => {
    closeModal()
    if (onExistingContact) onExistingContact(contact)
    else window.open(`/contacts?drawer=${contact.id}`, `_blank`, `noreferrer`)
  }

  const onSubmit = handleSubmit(
    async (raw) => {
      // Post-validation values: the heading number has already moved to cellPhone.
      const values = raw as QuickCreateOutput
      setSubmitting(true)
      try {
        const match = await recheckDuplicate(values.name, {
          cellPhone: values.cellPhone || undefined,
          email: values.email || undefined,
          locationId: values.locationId,
        })
        if (match?.blocking) return
        const input = {
          name: values.name,
          cellPhone: values.cellPhone || undefined,
          email: values.email || undefined,
          locationId: values.locationId,
          becameLeadDate: moment(values.becameLeadDate).format(`YYYY-MM-DD`),
          leadStatus: values.leadStatus,
          utmSource: values.utmSource || undefined,
          utmMedium: values.utmMedium || undefined,
          utmCampaign: values.utmCampaign || undefined,
          channel: values.channel || undefined,
          leadOwner: values.leadOwner,
        }
        debug(`new-contact`, `create`, input)
        const result = await contactStore.create(input)
        const contact = (result.data as { createContact?: ContactJson } | undefined)?.createContact
        if (!contact) {
          notifyError(`Failed to create contact${result.error?.message ? `: ${result.error.message}` : ``}`)
          return
        }
        notifySuccess(`${contact.name} created`)
        closeModal()
        onCreate(contact)
      } finally {
        setSubmitting(false)
      }
    },
    (errors) => {
      const first = firstProblemField(errors)
      if (!first) return
      notifyError(String(errors[first]?.message ?? `Check the form`))
      if (first === `name` || first === `cellPhone` || first === `email`) setFocus(first)
    },
  )

  const onRootKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === `Enter`) {
      e.preventDefault()
      bg(onSubmit())
    }
  }

  const needsLocationChoice = (locations?.length ?? 0) > 1
  const headingPlaceholder = !name && digitsOnly(cellPhone).length === 10 ? `Name` : `Name or phone number…`

  return (
    <form className="flex w-screen flex-col md:w-[560px]" onKeyDown={onRootKeyDown} onSubmit={onSubmit}>
      <div className="flex flex-col gap-3 px-6 pb-5 pt-5">
        {/* The heading input is the modal title (the caller passes hideTitle to showModal). */}
        <div className="flex items-start gap-3">
          <Input
            {...register(`name`, { onBlur: moveHeadingPhone })}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            type="text"
            autoComplete="off"
            placeholder={headingPlaceholder}
            className="h-auto rounded-none border-0 bg-transparent p-0 text-heading-sm text-strong shadow-none outline-none placeholder:text-minimal focus:border-transparent focus:ring-0 focus-visible:ring-0"
            onKeyDown={(e) => {
              if (e.key === `ArrowDown` && suggestions.length) {
                e.preventDefault()
                suggestionsRef.current?.focusFirst()
              }
            }}
          />
          <Button
            type="button"
            variant="subtle"
            size="icon-sm"
            onClick={() => closeModal()}
            aria-label="Close"
            // Out of the tab order so name, Tab, phone stays the path; Escape closes.
            tabIndex={-1}
            className="-mr-2 -mt-1"
          >
            <Xmark className="h-4 w-4" strokeWidth={2} />
          </Button>
        </div>

        {duplicate && (
          <DuplicateBanner
            match={duplicate}
            onOpen={() => openExisting(duplicate.contact)}
            onDismiss={() => dismiss(duplicate.contact)}
          />
        )}
        <ContactSuggestionList
          ref={suggestionsRef}
          contacts={suggestions}
          onOpen={openExisting}
          onLeaveUp={() => setFocus(`name`)}
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Input
            {...register(`cellPhone`, {
              onBlur: (e) => setValue(`cellPhone`, formatPhoneNumber(e.target.value) ?? ``),
            })}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            placeholder="Cell phone"
            aria-label="Cell phone"
          />
          <Input {...register(`email`)} type="email" autoComplete="off" placeholder="Email" aria-label="Email" />
        </div>
        <div className="-mt-2 text-body-xs text-subtle">Cell phone or email required.</div>

        {/* Lead status stays above the fold: the front desk sets it on the call. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {needsLocationChoice && (
            <OptionRow label="Location">
              <Controller
                name="locationId"
                control={control}
                render={({ field }) => (
                  <LocationPicker locations={locations ?? []} value={field.value} onChange={field.onChange} />
                )}
              />
            </OptionRow>
          )}
          <OptionRow label="Lead status">
            <Controller
              name="leadStatus"
              control={control}
              render={({ field }) => (
                <OptionPicker label={field.value} options={LEAD_STATUS_OPTIONS} onChange={field.onChange} />
              )}
            />
          </OptionRow>
        </div>

        {showMore && (
          <QuickContactOptions control={control} register={register} isSMC={isSMC} useUsersForOwner={true} />
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-default px-6 py-3">
        <Button type="button" variant="subtle" size="xs" onClick={() => setShowMore((v) => !v)} className="mr-auto">
          {showMore ? `Fewer options` : `More options`}
          {showMore ? (
            <NavArrowUp className="h-3 w-3" strokeWidth={2} />
          ) : (
            <NavArrowDown className="h-3 w-3" strokeWidth={2} />
          )}
        </Button>
        <Button type="button" variant="white" onClick={() => closeModal()}>
          Cancel
        </Button>
        <Button type="submit" loading={submitting || formState.isSubmitting} disabled={duplicate?.blocking}>
          Save
        </Button>
      </div>
    </form>
  )
})
